import { findUrls } from '@/lib/detect-urls';

// Remark plugin — the markdown analogue of `linkifyUrls`. GFM's autolink
// literals only fire on explicit `http(s)://` and `www.`-prefixed text, so a
// bare domain like `leanin.org/women-in-the-workplace` in rendered markdown
// stays plain, unclickable text. This plugin rewrites the URL spans `findUrls`
// detects in text nodes into link nodes (scheme-less matches normalized to
// https), so markdown surfaces link the same URL shapes as the plain-text
// ones (DM bubbles, legacy post bodies). Link nodes are not descended into,
// so URL-shaped fragments inside an authored link's label stay untouched.

type MdastNode = {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
};

export function remarkLinkifyUrls() {
  return (tree: MdastNode) => visit(tree);
}

function visit(node: MdastNode): void {
  const children = node.children;
  if (children === undefined) return;
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i]!;
    if (child.type === 'link' || child.type === 'linkReference') continue;
    if (child.type === 'text') {
      const replaced = splitUrls(child.value ?? '');
      if (replaced !== null) children.splice(i, 1, ...replaced);
      continue;
    }
    visit(child);
  }
}

function splitUrls(text: string): MdastNode[] | null {
  const matches = findUrls(text);
  if (matches.length === 0) return null;
  const out: MdastNode[] = [];
  let last = 0;
  for (const m of matches) {
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({ type: 'link', url: m.href, children: [{ type: 'text', value: m.raw }] });
    last = m.index + m.raw.length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}
